/**
 * FileManagement — Main Container
 *
 * Root component for the KB File Management feature.
 *
 * Navigation model:
 *   - ?view=files in URL        → controls entry/exit (survives refresh)
 *   - ?folder=<id> in URL       → tracks current folder (replace, no history pollution)
 *   - data.path from API        → ancestor chain that drives breadcrumbs + back button
 *
 * Back button logic (3 cases, checked in order):
 *   1. Inside a folder w/ path  → go to path[len-2] (or root if path has 1 item)
 *   2. ?folder set but no path  → go to root (loading / fallback)
 *   3. At root                  → exit to Knowledge Base
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import Button from "../../../../../../../components/Button/Button";
import RightArrowIcon from "../../../../../../../assets/svg/right-arrow-icon";
import AddIcon from "../../../../../../../assets/svg/add-icon";
import OpenFolderIcon from "../../../../../../../assets/svg/open-folder-icon";
import { useListKBFilesQuery } from "../../../../../../../redux/services/kbFilesApi";
import { openFileDetails } from "../../../../../../../redux/slices/fileManagementSlice";
import {
  DEFAULT_SORT,
  KB_FILES_LIMITS,
  FOLDER_HEADER_MENU_ITEMS,
} from "./constants/fileManagement.constants";
import {
  isFolder,
  copyToClipboardWithToast,
} from "./utils/fileManagement.utils";
import type {
  ViewMode,
  KBFileSortKey,
  KBFileSortOrder,
  KBFileEntity,
} from "./types/fileManagement.types";
import Breadcrumbs from "./components/Breadcrumbs";
import FileManagementHeader from "./components/FileManagementHeader";
import FileListView from "./components/FileListView";
import FileGridView from "./components/FileGridView";
import CreateFolderModal from "./components/FolderNameModal/components/CreateFolderModal";
import DeleteEntityModal from "./components/DeleteEntityModal";
import FileDetailsDrawer from "./components/FileDetailsDrawer/FileDetailsDrawer";
import UploadFilesDrawer from "./components/UploadFilesDrawer";
import RenameFolderModal from "./components/FolderNameModal/components/RenameFolderModal";
import ViewModeToggle from "./components/ViewModeToggle";
import FileSearchInput from "./components/FileSearchInput";
import RowActionsMenu from "./components/RowActionsMenu";

const FileManagement = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---------------------------------------------------------------------------
  // URL-driven state
  // ---------------------------------------------------------------------------

  /** Current folder ID from URL. null = root level. */
  const currentFolderId = searchParams.get("folder") || null;
  const isInsideFolder = currentFolderId !== null;

  // ---------------------------------------------------------------------------
  // Local UI state
  // ---------------------------------------------------------------------------

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortField, setSortField] = useState<KBFileSortKey>(
    DEFAULT_SORT.sort_key
  );
  const [sortOrder, setSortOrder] = useState<KBFileSortOrder>(
    DEFAULT_SORT.sort_order
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    isInsideFolder
      ? KB_FILES_LIMITS.DEFAULT_PAGE_SIZE
      : KB_FILES_LIMITS.MAX_PAGE_SIZE
  );
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<KBFileEntity | null>(null);
  const [renameTarget, setRenameTarget] = useState<KBFileEntity | null>(null);
  const [isUploadDrawerOpen, setIsUploadDrawerOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Debounced search (500ms) — only triggers API call when input has >= 3 chars.
  // Empty input clears the search; 1-2 chars are ignored.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const trimmedInput = searchInput.trim();
    if (trimmedInput?.length > 0 && trimmedInput?.length < 3) return;

    const timeout = setTimeout(() => {
      setSearchQuery(trimmedInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  // ---------------------------------------------------------------------------
  // API: list files in current folder
  // ---------------------------------------------------------------------------

  const { data, isFetching } = useListKBFilesQuery(
    {
      parent_folder_id: currentFolderId,
      page: currentPage,
      limit: rowsPerPage,
      sort_key: sortField,
      sort_order: sortOrder,
      search: searchQuery || undefined,
    },
    { refetchOnMountOrArgChange: true }
  );

  const items = data?.items ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  // ---------------------------------------------------------------------------
  // Folder path (ancestor chain) from the API — last item is the current folder
  // ---------------------------------------------------------------------------

  const folderPath = useMemo(() => data?.path ?? [], [data?.path]);
  const currentFolderItem = folderPath?.[folderPath?.length - 1] ?? null;
  const currentFolderName = currentFolderItem?.name ?? null;

  /**
   * Minimal entity stand-in for the folder-header three-dots menu.
   * Built from the API path so the menu stays in sync after refresh / deep-link.
   */
  const currentFolderEntity = currentFolderItem
    ? ({
        ...currentFolderItem,
        type: "folder",
      } as KBFileEntity)
    : null;

  // ---------------------------------------------------------------------------
  // Helper: update ?folder= URL param with replace (no history push)
  // ---------------------------------------------------------------------------

  const setFolderParam = useCallback(
    (folderId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (folderId) {
            next.set("folder", folderId);
          } else {
            next.delete("folder");
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // ---------------------------------------------------------------------------
  // Helper: reset page, search, and rows-per-page when navigating.
  // rowsPerPage default depends on next location AND current view mode:
  //   - inside a folder        -> 10  (DEFAULT_PAGE_SIZE)
  //   - root + grid view       -> 100 (MAX_PAGE_SIZE)
  //   - root + list view       -> 10  (DEFAULT_PAGE_SIZE)
  // ---------------------------------------------------------------------------

  const resetListState = useCallback(
    (nextIsInsideFolder: boolean) => {
      setCurrentPage(1);
      setSearchInput("");
      setSearchQuery("");
      const useDefaultSize = nextIsInsideFolder || viewMode === "list";
      setRowsPerPage(
        useDefaultSize
          ? KB_FILES_LIMITS.DEFAULT_PAGE_SIZE
          : KB_FILES_LIMITS.MAX_PAGE_SIZE
      );
    },
    [viewMode]
  );

  // ---------------------------------------------------------------------------
  // Navigation: click a folder (drill down)
  // ---------------------------------------------------------------------------

  const handleFolderClick = useCallback(
    (folder: KBFileEntity) => {
      if (!isFolder(folder)) return;
      resetListState(true);
      setFolderParam(folder?._id);
    },
    [setFolderParam, resetListState]
  );

  // ---------------------------------------------------------------------------
  // Navigation: back button (go up one level)
  //
  // Case 1: Path has items → go to path[len-2] (or root if path has 1 item)
  // Case 2: ?folder set but path not yet loaded → go to root (fallback)
  // Case 3: At root → exit to Knowledge Base
  // ---------------------------------------------------------------------------

  const handleGoBack = useCallback(() => {
    if (folderPath?.length > 0) {
      const parent = folderPath[folderPath.length - 2] ?? null;
      resetListState(Boolean(parent));
      setFolderParam(parent ? parent._id : null);
      return;
    }

    if (currentFolderId) {
      resetListState(false);
      setFolderParam(null);
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("view");
        next.delete("folder");
        return next;
      },
      { replace: true }
    );
  }, [
    folderPath,
    currentFolderId,
    setFolderParam,
    setSearchParams,
    resetListState,
  ]);

  // ---------------------------------------------------------------------------
  // Navigation: breadcrumb "Home" click (go to root)
  // ---------------------------------------------------------------------------

  const handleNavigateToRoot = useCallback(() => {
    resetListState(false);
    setFolderParam(null);
  }, [setFolderParam, resetListState]);

  // ---------------------------------------------------------------------------
  // Navigation: breadcrumb folder click (jump to specific ancestor)
  // ---------------------------------------------------------------------------

  const handleNavigateToFolder = useCallback(
    (folderIndex: number) => {
      const targetFolder = folderPath?.[folderIndex];
      if (!targetFolder) return;
      resetListState(true);
      setFolderParam(targetFolder._id);
    },
    [folderPath, setFolderParam, resetListState]
  );

  // ---------------------------------------------------------------------------
  // Sort toggle (same pattern as ManageDocumentsTable)
  // ---------------------------------------------------------------------------

  const handleSort = useCallback(
    (field: KBFileSortKey, order: KBFileSortOrder) => {
      setSortField(field);
      setSortOrder(order);
      setCurrentPage(1);
    },
    []
  );

  // ---------------------------------------------------------------------------
  // View mode change: grid defaults to 100 rows, list defaults to 10 rows
  // ---------------------------------------------------------------------------

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    setRowsPerPage(
      mode === "grid"
        ? KB_FILES_LIMITS.MAX_PAGE_SIZE
        : KB_FILES_LIMITS.DEFAULT_PAGE_SIZE
    );
    setCurrentPage(1);
  }, []);

  // ---------------------------------------------------------------------------
  // File click: open the file details drawer for non-folder items
  // ---------------------------------------------------------------------------

  const handleFileClick = useCallback(
    (file: KBFileEntity) => {
      if (isFolder(file)) return;
      dispatch(openFileDetails(file));
    },
    [dispatch]
  );

  // ---------------------------------------------------------------------------
  // Row action: handle three-dots menu actions for files & folders
  // ---------------------------------------------------------------------------

  const handleRowAction = useCallback(
    (item: KBFileEntity, action: string) => {
      switch (action) {
        case "view_details":
          if (!isFolder(item)) dispatch(openFileDetails(item));
          break;
        case "open":
          if (isFolder(item)) handleFolderClick(item);
          break;
        case "rename":
          if (isFolder(item)) {
            setRenameTarget(item);
          } else {
            dispatch(openFileDetails(item));
          }
          break;
        case "copy_link":
          if (!isFolder(item) && item?.short_url) {
            copyToClipboardWithToast(item.short_url);
          }
          break;
        case "delete":
          setDeleteTarget(item);
          break;
        default:
          break;
      }
    },
    [dispatch, handleFolderClick]
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Action Bar: ← Back + Upload Files + Create Folder ............... [Grid | List] */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            innerText=""
            iconLeft={true}
            Icon={RightArrowIcon}
            buttonContainerClass="w-fit"
            iconClass="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk rotate-180"
            onClick={handleGoBack}
            type="button"
          />
          <Button
            Icon={AddIcon}
            iconLeft={true}
            iconClass="w-4 h-4 text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk"
            innerText="Upload Files"
            onClick={() => setIsUploadDrawerOpen(true)}
            buttonContainerClass="hover:bg-verba-blue-hover dark:hover:bg-verba-blue-hover-dk border border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk w-fit font-semibold py-[3px] px-4 rounded-[6px]"
            innerTextClass="text-fixed-white-light-and-dark-mode dark:text-fixed-white-light-and-dark-mode-dk text-md font-semibold text-nowrap"
          />
          <Button
            innerText="Create Folder"
            onClick={() => setIsCreateFolderOpen(true)}
            Icon={OpenFolderIcon}
            iconLeft={true}
            iconClass="w-4 h-4 text-analytics-gray dark:text-analytics-gray-dk"
            buttonContainerClass="w-fit font-semibold py-[3px] px-[11px] rounded-[6px] border border-analytics-gray dark:border-analytics-gray-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
            innerTextClass="text-md font-semibold text-analytics-gray dark:text-analytics-gray-dk text-nowrap"
          />
        </div>

        <ViewModeToggle viewMode={viewMode} onChange={handleViewModeChange} />
      </div>

      {/* Search + Breadcrumbs row */}
      <div className="flex items-center gap-[18px] py-1">
        <FileSearchInput value={searchInput} onChange={setSearchInput} />
        {folderPath?.length > 0 && (
          <>
            <span className="w-px h-3.5 bg-border-gray dark:bg-border-gray-dk" />
            <Breadcrumbs
              path={folderPath}
              onNavigateToRoot={handleNavigateToRoot}
              onNavigateToFolder={handleNavigateToFolder}
            />
          </>
        )}
      </div>

      {/* Folder Name Header (only visible inside a folder) */}
      {isInsideFolder && currentFolderName && (
        <div className="flex items-center gap-2">
          <OpenFolderIcon className="w-7 h-7 text-analytics-blue dark:text-analytics-blue-dk" />
          <h2 className="text-xl font-medium text-heading-text dark:text-heading-text-dk">
            {currentFolderName}
          </h2>
          {currentFolderEntity && FOLDER_HEADER_MENU_ITEMS?.length > 0 && (
            <RowActionsMenu
              entity={currentFolderEntity}
              menuItems={FOLDER_HEADER_MENU_ITEMS}
              onRowAction={handleRowAction}
            />
          )}
        </div>
      )}

      {/* Title: All Files & Folders <count> — only at root level in list view */}
      {!isInsideFolder && viewMode === "list" && (
        <FileManagementHeader totalCount={totalCount} />
      )}

      {/* Content: List or Grid View */}
      {viewMode === "list" ? (
        <FileListView
          items={items}
          isFetching={isFetching}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
          onFolderClick={handleFolderClick}
          onFileClick={handleFileClick}
          onRowAction={handleRowAction}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalPages={totalPages}
          onPageChange={(page: number) => setCurrentPage(page)}
          onRowsPerPageChange={(size: number) => setRowsPerPage(size)}
        />
      ) : (
        <FileGridView
          items={items}
          isFetching={isFetching}
          onFolderClick={handleFolderClick}
          onFileClick={handleFileClick}
          onRowAction={handleRowAction}
          isInsideFolder={isInsideFolder}
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalPages={totalPages}
          onPageChange={(page: number) => setCurrentPage(page)}
          onRowsPerPageChange={(size: number) => setRowsPerPage(size)}
        />
      )}

      {/* Create Folder Modal */}
      <CreateFolderModal
        open={isCreateFolderOpen}
        onOpenChange={setIsCreateFolderOpen}
        parentFolderId={currentFolderId}
      />

      {/* Delete Modal */}
      <DeleteEntityModal
        open={deleteTarget !== null}
        onOpenChange={() => setDeleteTarget(null)}
        entity={deleteTarget}
      />

      {/* Rename Folder Modal */}
      <RenameFolderModal
        open={renameTarget !== null}
        onOpenChange={() => setRenameTarget(null)}
        entity={renameTarget}
      />

      {/* File Details Drawer */}
      <FileDetailsDrawer path={folderPath} viewMode={viewMode} />

      {/* Upload Files Drawer */}
      <UploadFilesDrawer
        open={isUploadDrawerOpen}
        onClose={() => setIsUploadDrawerOpen(false)}
        parentFolderId={currentFolderId}
      />
    </div>
  );
};

export default FileManagement;
